<template>
  <Teleport to="body">
    <div v-if="show" class="dir-viewer-overlay eh-popup-overlay" @click.self="onClose">
      <div class="dir-viewer eh-popup-panel eh-ds" data-cy="doc-viewer">
        <div class="dir-viewer-header">
          <div>
            <h3 class="dir-viewer-title">
              <span class="glyphicon glyphicon-file bi bi-file-earmark-text"></span>
              {{ title || 'Document' }}
            </h3>
            <!-- Who this is about, pinned in the header: name · MRN · DOB. -->
            <div v-if="patientLine" class="dir-viewer-patient" data-cy="modal-patient">{{ patientLine }}</div>
          </div>
          <div class="dir-viewer-actions">
            <!-- Reading the document is exactly when a reviewer works out which
                 directives it contains, so the coding action lives here too —
                 not only behind the record modal's action row. -->
            <button
              v-if="canAddDirectives"
              class="dir-viewer-add"
              data-cy="viewer-add-directives"
              @click="onAddDirectives"
            >
              <span class="glyphicon glyphicon-plus bi bi-plus-lg"></span>
              Add directives
            </button>
            <a
              v-if="url"
              :href="url"
              target="_blank"
              rel="noopener"
              class="dir-viewer-newtab"
              title="Open in a new tab"
            >
              <span class="glyphicon glyphicon-new-window bi bi-box-arrow-up-right"></span>
              Open in new tab
            </a>
            <button class="dir-close-btn" @click="onClose" aria-label="Close">
              <span class="glyphicon glyphicon-remove bi bi-x-lg"></span>
            </button>
          </div>
        </div>
        <div class="dir-viewer-body">
          <div v-if="!url || failed" class="dir-viewer-unavailable">{{ failedText }}</div>
          <img
            v-else-if="isImage && src"
            :src="src"
            :alt="title || 'Document'"
            class="dir-viewer-img"
            @error="failed = true"
          />
          <iframe v-else-if="src" :src="src" class="dir-viewer-frame" title="Document preview"></iframe>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
// Inline document viewer kept on the same route: a PDF renders in an <iframe>
// and an uploaded camera image as <img>, both by the record's opaque file uid.
// Which one is decided by the row's content type, never the URL. Sits above
// the document-history modal.
//
// `url` is the file reference (keyonFileUrl); what the element loads is a
// single-use content ticket URL resolved from it when the viewer opens
// (api2/fileTickets.js), because an <img> or <iframe> cannot send the bearer
// token. A ticket reads once, so it is asked for per opening, not per render.
import { computed, ref, watch } from "vue";
import { isImageContentType } from "@/utils/directiveDocument";
import { DOCUMENT_UNAVAILABLE_TEXT, SCAN_PENDING_TEXT } from "@/utils/fileUrl";
import { resolveFileUrl } from "@/api2/fileTickets";

const props = defineProps({
  show: { type: Boolean, default: false },
  url: { type: String, default: "" },
  title: { type: String, default: "" },
  // The parent owns the privilege/status decision (canAddDirectivesTo) — this
  // viewer is also opened for records that can't be coded, so it must not
  // assume the button is always appropriate.
  canAddDirectives: { type: Boolean, default: false },
  /** Preformatted "name · MRN · DOB" header line from the parent page. */
  patientLine: { type: String, default: "" },
  /** The document row's content type (document_content_type). */
  contentType: { type: String, default: "" },
});
const emit = defineEmits(["close", "add-directives"]);

const isImage = computed(() => isImageContentType(props.contentType));
// An <img> that fails to load, or a file the API refuses (410, a lapsed
// second factor), says so instead of showing a broken image.
const failed = ref(false);
const refusal = ref("");
const src = ref("");
const failedText = computed(() => (refusal.value === "scan_pending" ? SCAN_PENDING_TEXT : DOCUMENT_UNAVAILABLE_TEXT));
let opening = 0;

async function resolveSource() {
  const token = ++opening;
  failed.value = false;
  refusal.value = "";
  src.value = "";
  if (!props.show || !props.url) return;
  const resolved = await resolveFileUrl(props.url);
  if (token !== opening) return;
  if (resolved.ok) {
    src.value = resolved.url;
  } else {
    refusal.value = resolved.state;
    failed.value = true;
  }
}
watch(() => [props.url, props.show], resolveSource, { immediate: true });

function onClose() {
  emit("close");
}
// Close first: the assigner is its own full-screen surface and shows the same
// document in its own pane, so leaving this overlay stacked underneath would
// trap the body scroll lock when the assigner closes.
function onAddDirectives() {
  emit("close");
  emit("add-directives");
}
</script>

<style scoped>
.dir-viewer-unavailable {
  color: #fff;
  font-size: 16px;
  padding: 40px 20px;
  text-align: center;
}
.dir-viewer-overlay {
  /* Backdrop color + z-index from brand-ds (.eh-popup-overlay / --eh-popup-*). */
  position: fixed;
  inset: 0;
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}
.dir-viewer {
  /* Surface, border, radius from brand-ds (.eh-popup-panel / --eh-popup-*). */
  width: 100%;
  max-width: 1000px;
  height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.dir-viewer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 18px;
  border-bottom: 1px solid var(--eh-divider);
}
.dir-viewer-title {
  margin: 0;
  font-size: 16px;
  color: var(--eh-teal-ink);
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* Patient identity line pinned in the header (name · MRN · DOB). */
.dir-viewer-patient {
  margin-top: 2px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--eh-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dir-viewer-actions {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-shrink: 0;
}
.dir-viewer-newtab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: var(--eh-teal);
  text-decoration: none;
}
.dir-viewer-newtab:hover {
  text-decoration: underline;
}
.dir-viewer-add {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: var(--eh-white);
  background: var(--eh-teal);
  border: none;
  border-radius: 6px;
  padding: 7px 12px;
  cursor: pointer;
}
.dir-viewer-add:hover {
  background: var(--eh-teal-deep);
}
.dir-close-btn {
  border: none;
  background: transparent;
  font-size: 16px;
  color: var(--eh-muted);
  cursor: pointer;
}
.dir-viewer-body {
  flex: 1;
  min-height: 0;
  background: var(--eh-muted-fill);
  display: flex;
  align-items: center;
  justify-content: center;
}
.dir-viewer-frame {
  width: 100%;
  height: 100%;
  border: none;
}
.dir-viewer-img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}
@media (max-width: 600px) {
  .dir-viewer-overlay {
    padding: 0;
  }
  .dir-viewer {
    max-width: none;
    width: 100%;
    height: 100%;
    border-radius: 0;
  }
}
</style>
